'use client';

import { useSearchParams } from 'next/navigation';
import React, { useState, useCallback, useMemo } from 'react';
import BigNumber from 'bignumber.js';
import { MdToken } from 'react-icons/md';

import useQueryWithPages from 'src/hooks/useQueryWithPages';

import styles from './TokensPage.module.css';
import FilterDropdown from 'src/app/components/FilterDropdown';
import type { FilterSection } from 'src/app/components/FilterDropdown';

const TOKEN_TYPE_OPTIONS: FilterSection = {
  title: 'Token Type',
  type: 'checkbox',
  key: 'type',
  options: [
    { value: 'ORC-20', label: 'ORC-20' },
    { value: 'ORC-721', label: 'ORC-721' },
    { value: 'ORC-1155', label: 'ORC-1155' },
    { value: 'ORC-404', label: 'ORC-404' },
  ],
};

const ITEMS_PER_PAGE = 50;

const TokensPage: React.FC = () => {
  const searchParams = useSearchParams();
  const [searchQuery, setSearchQuery] = React.useState('');
  const [redirecting, setRedirecting] = React.useState(false);
  const [filterValues, setFilterValues] = useState<Record<string, Array<string>>>({ type: [] });

  const handleFilterApply = useCallback((values: Record<string, Array<string>>) => {
    setFilterValues(values);
  }, []);

  const handleFilterReset = useCallback(() => {
    setFilterValues({ type: [] });
  }, []);

  // Check if direct page access has valid cursor
  React.useEffect(() => {
    const pageParam = searchParams.get('page');
    const requestedPage = pageParam ? parseInt(pageParam, 10) : 1;

    if (requestedPage > 1 && !redirecting) {
      try {
        const cursors = JSON.parse(sessionStorage.getItem('tokens-cursors') || '{}');
        const hasCursor = cursors[requestedPage];

        // No cursor for this page - redirect to page 1
        if (!hasCursor) {
          setRedirecting(true);
          window.location.href = '/tokens';
          return;
        }
      } catch (e) {
        // Storage error - redirect to page 1
        setRedirecting(true);
        window.location.href = '/tokens';
        return;
      }
    }
  }, [searchParams, redirecting]);

  const tokensFilters = useMemo(() => {
    const f: Record<string, any> = { q: searchQuery || undefined };
    if (filterValues.type?.length) f.type = filterValues.type;
    return f;
  }, [searchQuery, filterValues]);

  const tokensQuery = useQueryWithPages({
    resourceName: 'tokens',
    filters: tokensFilters,
  });

  const tokens = React.useMemo(() => {
    return tokensQuery.data?.items || [];
  }, [tokensQuery.data?.items]);

  // Store next page cursor for invalid page detection
  React.useEffect(() => {
    const currentPage = tokensQuery.pagination.page;
    const nextPageParams = tokensQuery.data?.next_page_params;

    if (nextPageParams) {
      try {
        const cursors = JSON.parse(sessionStorage.getItem('tokens-cursors') || '{}');
        cursors[currentPage + 1] = nextPageParams;
        cursors._timestamp = Date.now();
        sessionStorage.setItem('tokens-cursors', JSON.stringify(cursors));
      } catch (e) {
        // Ignore storage errors
      }
    }
  }, [tokensQuery.pagination.page, tokensQuery.data?.next_page_params]);

  // Clear cursors when search changes
  React.useEffect(() => {
    try {
      sessionStorage.removeItem('tokens-cursors');
    } catch (e) {
      // Ignore
    }
  }, [searchQuery]);

  // Show redirecting message
  if (redirecting) {
    return (
      <div className={ styles.container }>
        <div style={{ textAlign: 'center', padding: '4rem', color: 'rgba(255,255,255,0.6)' }}>
          <p style={{ fontSize: '1.2rem', marginBottom: '1rem' }}>Invalid page number</p>
          <p>Redirecting to page 1...</p>
        </div>
      </div>
    );
  }

  return (
    <div className={ styles.container }>
      <div className={ styles.header }>
        <h1 className={ styles.title }>Tokens</h1>
        <FilterDropdown
          label="Filter"
          sections={[TOKEN_TYPE_OPTIONS]}
          values={filterValues}
          onApply={handleFilterApply}
          onReset={handleFilterReset}
        />
      </div>

      <div className={ styles.tableContainer }>
        <table className={ styles.table }>
          <thead>
            <tr>
              <th>#</th>
              <th>Token</th>
              <th>Type</th>
              <th>Price</th>
              <th>Market Cap</th>
              <th>Total Supply</th>
              <th>Holders</th>
            </tr>
          </thead>
          <tbody>
            { tokensQuery.isLoading ? (
              Array.from({ length: 10 }).map((_, i) => (
                <tr key={ i }>
                  <td><div className={ styles.skeleton } style={{ width: '1.5rem', height: '0.875rem' }} /></td>
                  <td>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', minWidth: 0 }}>
                      <div className={ styles.skeleton } style={{ width: '1.5rem', height: '1.5rem', borderRadius: '50%', flexShrink: 0 }} />
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
                        <div className={ styles.skeleton } style={{ width: '6rem', height: '0.875rem', display: 'block' }} />
                        <div className={ styles.skeleton } style={{ width: '3rem', height: '0.75rem', display: 'block' }} />
                      </div>
                    </div>
                  </td>
                  <td><div className={ styles.skeleton } style={{ width: '3.5rem', height: '0.875rem' }} /></td>
                  <td><div className={ styles.skeleton } style={{ width: '4rem', height: '0.875rem' }} /></td>
                  <td><div className={ styles.skeleton } style={{ width: '5rem', height: '0.875rem' }} /></td>
                  <td><div className={ styles.skeleton } style={{ width: '5rem', height: '0.875rem' }} /></td>
                  <td><div className={ styles.skeleton } style={{ width: '3rem', height: '0.875rem' }} /></td>
                </tr>
              ))
            ) : tokens.length === 0 ? (
              <tr>
                <td colSpan={ 7 }>
                  <div className={ styles.emptyState }>
                    <MdToken size={ 48 }/>
                    <p>No tokens found.</p>
                  </div>
                </td>
              </tr>
            ) : (
              tokens.map((token: any, index: number) => {
                const formatSupply = (supply: string | null, decimals: string | null) => {
                  if (!supply) return '-';
                  const dec = decimals ? parseInt(decimals, 10) : 18;
                  const bn = BigNumber(supply).dividedBy(BigNumber(10).pow(dec));
                  return bn.toFormat(0);
                };
                const formatMarketCap = (cap: string | null) => {
                  if (!cap) return '-';
                  const num = Number(cap);
                  if (num >= 1000000) return `$${(num / 1000000).toFixed(2)}M`;
                  if (num >= 1000) return `$${(num / 1000).toFixed(2)}K`;
                  return `$${num.toFixed(2)}`;
                };
                return (
                  <tr key={ token.address || index }>
                    <td>{ (tokensQuery.pagination.page - 1) * ITEMS_PER_PAGE + index + 1 }</td>
                    <td>
                      <div className={ styles.tokenInfo }>
                        { token.icon_url ? (
                          <img src={ token.icon_url } alt={ token.name } className={ styles.tokenIcon }/>
                        ) : (
                          <div className={ styles.tokenIconPlaceholder }>
                            <MdToken size={ 18 }/>
                          </div>
                        ) }
                        <div>
                          <a href={ `/token/${ token.address }` } className={ styles.tokenName }>
                            { token.name || 'Unknown' }
                          </a>
                          <div className={ styles.tokenSymbol }>{ token.symbol || '' }</div>
                        </div>
                      </div>
                    </td>
                    <td><span className={ styles.typeBadge }>{ token.type || '-' }</span></td>
                    <td>{ token.exchange_rate ? `$${token.exchange_rate}` : '-' }</td>
                    <td>{ formatMarketCap(token.circulating_market_cap) }</td>
                    <td>{ formatSupply(token.total_supply, token.decimals) } { token.symbol || '' }</td>
                    <td>{ token.holders ? Number(token.holders).toLocaleString() : '0' }</td>
                  </tr>
                );
              })
            ) }
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      { tokens.length > 0 && (
        <div className={ styles.pagination }>
          <button
            className={ styles.pageButton }
            disabled={ tokensQuery.pagination.page === 1 }
            onClick={ () => {
              if (tokensQuery.pagination.page !== 1) {
                window.location.href = '/tokens';
              }
            } }
          >
            First
          </button>
          <button
            className={ styles.pageButton }
            disabled={ !tokensQuery.pagination.canGoBackwards }
            onClick={ tokensQuery.pagination.onPrevPageClick }
          >
            &lt;
          </button>
          <button className={ `${ styles.pageButton } ${ styles.pageButtonActive }` }>
            { tokensQuery.pagination.page }
          </button>
          <button
            className={ styles.pageButton }
            disabled={ !tokensQuery.pagination.hasNextPage }
            onClick={ tokensQuery.pagination.onNextPageClick }
          >
            &gt;
          </button>
        </div>
      ) }
    </div>
  );
};

export default TokensPage;
